import { z } from 'zod';

/**
 * Политика действия на кассе:
 * allowed — можно; self_pin — подтверждение своим PIN;
 * elevated_pin — PIN менеджера; denied — нельзя никому на этой роли.
 */
export const Policy = z.enum(['allowed', 'self_pin', 'elevated_pin', 'denied']);
export type Policy = z.infer<typeof Policy>;

/**
 * Права сотрудника.
 *
 * Список закрытый: каждое право — отдельное решение владельца, и склеивать
 * их нельзя. Доступ к чужим столам когда-то прикрывался правом «снимать
 * позиции» — и менеджер, которому разрешили снимать, молча получал чужие
 * столы. Разные решения должны спрашиваться разными вопросами.
 *
 * Порядок здесь — порядок в интерфейсе: сначала зал, потом деньги,
 * потом склад и офис. Так владелец читает список сверху вниз и понимает,
 * что даёт.
 */
export const PermissionKey = z.enum([
  // ── зал и заказы ──
  'order.create',
  /** открывать и править счета на столах, созданных другими */
  'table.any',
  /** назначать другого сотрудника ответственным за открытый счёт */
  'table.reassign',
  /** переносить и объединять столы */
  'table.manage',
  /** вернуть закрытый счёт в работу: гость решил дозаказать */
  'table.unbill',
  'order.item.remove',
  'order.cancel',
  /**
   * Удалить ошибочно открытый стол.
   *
   * Отмена и удаление — разные вещи. Отменённый чек остаётся в отчёте
   * со следом и причиной: он был, гость его видел. Ошибочно открытый
   * стол — заказ, которого не должно было существовать: официант
   * промахнулся по схеме зала и открыл соседний
   */
  'order.delete',
  'order.discount',

  // ── деньги ──
  'order.pay',
  'order.refund',
  /** открывать смену; закрывать — отдельным правом ниже */
  'cash.shift.open',
  'cash.shift.close',
  /** открывать денежный ящик без продажи */
  'cash.drawer',
  /** править уже закрытые чеки — история, трогать её опасно */
  'order.edit.closed',

  // ── работа зала ──
  /** останавливать продажу блюда и задавать остаток */
  'stoplist.manage',
  /** заводить гостя прямо на кассе */
  'guest.create',
  /** выходить из режима официанта в кассовый */
  'pos.mode.switch',

  // ── склад ──
  /** фиксировать итоги пересчёта */
  'stock.count',
  /** проводить расходы и списания */
  'stock.writeoff',

  // ── офис ──
  /** видеть выручку, оплаты и маржу */
  'reports.finance',
  /** управлять сотрудниками и их доступом */
  'staff.manage',
  /** менять критичные настройки заведения */
  'settings.manage',
]);
export type PermissionKey = z.infer<typeof PermissionKey>;

/**
 * Что означает право — словами для владельца.
 *
 * Держим рядом с ключами: список прав растёт, и описание, живущее
 * в другом файле, однажды отстанет от него.
 */
export const PERMISSION_INFO: Record<string, { title: string; hint: string; group: string }> = {
  'order.create': {
    group: 'Зал',
    title: 'Приём заказов',
    hint: 'Набирать заказ и отправлять его на кухню.',
  },
  'table.any': {
    group: 'Зал',
    title: 'Доступ ко всем столам',
    hint: 'Открывать и править счета на столах, созданных другими сотрудниками.',
  },
  'table.reassign': {
    group: 'Зал',
    title: 'Смена сотрудника счёта',
    hint: 'Назначать другого ответственным за открытый счёт: официант ушёл, гость остался.',
  },
  'table.manage': {
    group: 'Зал',
    title: 'Управление столами',
    hint: 'Переносить заказы между столами и объединять компании.',
  },
  'table.unbill': {
    group: 'Зал',
    title: 'Возврат счёта в работу',
    hint: 'Гость получил счёт, но решил дозаказать. Стол снова открывается официанту, счёт печатается заново.',
  },
  'order.item.remove': {
    group: 'Зал',
    title: 'Снятие позиции',
    hint: 'Убирать блюдо из счёта с указанием причины.',
  },
  'order.cancel': {
    group: 'Зал',
    title: 'Отмена заказа',
    hint: 'Отменять закрытый чек с причиной. След остаётся в отчёте — гость этот чек видел.',
  },
  'order.delete': {
    group: 'Зал',
    title: 'Удаление заказа',
    hint: 'Убирать ошибочно открытый стол: промахнулись по схеме зала и открыли соседний.',
  },
  'order.discount': {
    group: 'Зал',
    title: 'Скидки',
    hint: 'Применять скидки, бонусы и ручные корректировки цены.',
  },

  'order.pay': {
    group: 'Деньги',
    title: 'Оплата счетов',
    hint: 'Принимать деньги и закрывать счёт.',
  },
  'order.refund': {
    group: 'Деньги',
    title: 'Возвраты',
    hint: 'Оформлять возврат оплаты гостю.',
  },
  'cash.shift.open': {
    group: 'Деньги',
    title: 'Открытие смены',
    hint: 'Начинать кассовую смену и вносить размен.',
  },
  'cash.shift.close': {
    group: 'Деньги',
    title: 'Закрытие смены',
    hint: 'Считать кассу и закрывать смену. Здесь сходится вся выручка дня.',
  },
  'cash.drawer': {
    group: 'Деньги',
    title: 'Денежный ящик',
    hint: 'Открывать ящик без продажи — размен, инкассация.',
  },
  'order.edit.closed': {
    group: 'Деньги',
    title: 'Правка закрытых чеков',
    hint: 'Исправлять уже закрытые счета. Чек попал в отчёт и в фискальную очередь — правка меняет историю.',
  },

  'stoplist.manage': {
    group: 'Работа зала',
    title: 'Стоп-лист',
    hint: 'Останавливать продажу блюда и задавать остаток порций.',
  },
  'guest.create': {
    group: 'Работа зала',
    title: 'Добавление гостей',
    hint: 'Заводить нового гостя прямо на кассе.',
  },
  'pos.mode.switch': {
    group: 'Работа зала',
    title: 'Выход из режима продаж',
    hint: 'Переключать кассу из режима официанта в кассовый.',
  },

  'stock.count': {
    group: 'Склад',
    title: 'Инвентаризация',
    hint: 'Фиксировать итоги пересчёта — остатки станут такими, как посчитали.',
  },
  'stock.writeoff': {
    group: 'Склад',
    title: 'Списание',
    hint: 'Проводить расходы и списания со склада.',
  },

  'reports.finance': {
    group: 'Офис',
    title: 'Финансовые отчёты',
    hint: 'Видеть выручку, оплаты и маржу.',
  },
  'staff.manage': {
    group: 'Офис',
    title: 'Сотрудники и роли',
    hint: 'Заводить людей и раздавать доступ. Даёт власть выдать права кому угодно, включая себя.',
  },
  'settings.manage': {
    group: 'Офис',
    title: 'Настройки заведения',
    hint: 'Менять формат бизнеса, обслуживание, реквизиты.',
  },
};

/** Порядок групп в интерфейсе: от частого к редкому. */
export const PERMISSION_GROUPS = ['Зал', 'Деньги', 'Работа зала', 'Склад', 'Офис'] as const;

/**
 * Карта прав роли: ключ → политика.
 *
 * Ключи не ограничены enum-ом намеренно: роль, созданная год назад,
 * не знает о правах, введённых позже, и падать на них нельзя.
 * Как трактовать незнакомый ключ — решает policyOf в ядре.
 */
export const PermissionMap = z.record(z.string(), Policy);
export type PermissionMap = z.infer<typeof PermissionMap>;

export const ElevateRequest = z.object({
  pin: z.string().regex(/^\d{4,6}$/),
  /** что именно одобряет менеджер — право проверяется под это действие */
  action: PermissionKey,
});
export type ElevateRequest = z.infer<typeof ElevateRequest>;

export const ElevateResponse = z.object({
  ok: z.literal(true),
  approver: z.string(),
  /** короткоживущая подпись сервера: доказательство, что PIN действительно был */
  approverToken: z.string().optional(),
});
export type ElevateResponse = z.infer<typeof ElevateResponse>;
